// File: frontend/src/pages/SuperAdmin/components/SystemSettings/CategoryManagement.js

import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faEdit, 
  faTrashAlt, 
  faPlus, 
  faSync, 
  faSearch,
  faTimes,
  faHashtag
} from '@fortawesome/free-solid-svg-icons';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import { settingsService } from '../../../../../services/superAdmin/settingsService';
import styles from './CategoryManagement.module.css';

// Storage utility functions
const storageUtils = {
  getCachedCategoryTypes: () => {
    try {
      const cachedData = localStorage.getItem('categoryTypes');
      if (!cachedData) return null;
      
      const { data, timestamp } = JSON.parse(cachedData);
      const now = new Date().getTime();
      const hoursSinceLastFetch = (now - timestamp) / (1000 * 60 * 60);
      
      // Return data if it's less than 24 hours old
      if (hoursSinceLastFetch < 24) {
        return data;
      }
      return null;
    } catch (error) {
      console.error('Error retrieving cached category types:', error);
      return null;
    }
  },
  
  setCachedCategoryTypes: (data) => {
    try {
      const cacheData = {
        data,
        timestamp: new Date().getTime()
      };
      localStorage.setItem('categoryTypes', JSON.stringify(cacheData));
    } catch (error) {
      console.error('Error caching category types:', error);
    }
  },
  
  clearCachedCategoryTypes: () => {
    try {
      localStorage.removeItem('categoryTypes');
    } catch (error) {
      console.error('Error clearing cached category types:', error);
    }
  }
};

const CategoryManagement = ({ loading, setLoading, error, setError, success, setSuccess }) => {
  const { t } = useLanguage();
  
  // Category Management
  const [categoryTypes, setCategoryTypes] = useState([]);
  const [filteredCategoryTypes, setFilteredCategoryTypes] = useState([]);
  const [selectedCategoryType, setSelectedCategoryType] = useState(null);
  const [categoryValues, setCategoryValues] = useState([]);
  const [editingCategoryType, setEditingCategoryType] = useState(false);
  const [editingCategoryValue, setEditingCategoryValue] = useState(false);
  const [newCategoryType, setNewCategoryType] = useState({
    category_name: '',
    description: ''
  });
  const [newCategoryValue, setNewCategoryValue] = useState({
    value_key: '',
    is_active: 'true',
    description: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [loadingValues, setLoadingValues] = useState(false);

  // Fetch category types with caching
  const fetchCategoryTypes = async (forceRefresh = false) => {
    if (loading && !forceRefresh) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // Check cache first if not forcing refresh
      if (!forceRefresh) {
        const cachedData = storageUtils.getCachedCategoryTypes();
        if (cachedData) {
          console.log('Using cached category types');
          setCategoryTypes(cachedData);
          setFilteredCategoryTypes(sortCategoryTypes(cachedData));
          setLoading(false);
          return;
        }
      }
      
      console.log('Fetching category types from API...');
      setIsRefreshing(true);
      
      // Request all category types (up to 1000)
      const response = await settingsService.getCategoryTypes({
        limit: 1000,
        search: searchTerm
      });
      
      console.log('Category types response:', response);
      
      // Extract category types from the response
      const categoryTypesData = response.category_types || 
                              (response.data && response.data.category_types) || 
                              [];
      
      // Cache the data if not searching
      if (!searchTerm) {
        storageUtils.setCachedCategoryTypes(categoryTypesData);
      }
      
      setCategoryTypes(categoryTypesData);
      setFilteredCategoryTypes(sortCategoryTypes(categoryTypesData));
    } catch (err) {
      console.error('Error fetching category types:', err);
      setError(t('failed_to_load_category_types'));
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  // Sort category types alphabetically by name
  const sortCategoryTypes = (types) => {
    return [...types].sort((a, b) => 
      a.category_name.localeCompare(b.category_name)
    );
  };

  // Filter category types based on search term
  const filterCategoryTypes = (term) => {
    if (!term.trim()) {
      setFilteredCategoryTypes(sortCategoryTypes(categoryTypes));
      return;
    }
    
    const filtered = categoryTypes.filter(type => 
      type.category_name.toLowerCase().includes(term.toLowerCase()) ||
      (type.description && type.description.toLowerCase().includes(term.toLowerCase()))
    );
    
    setFilteredCategoryTypes(sortCategoryTypes(filtered));
  };

  // Fetch category values for a specific type
  const fetchCategoryValues = async (categoryTypeId) => {
    if (!categoryTypeId) {
      setCategoryValues([]);
      return;
    }
    
    setLoadingValues(true);
    setError(null);
    
    try {
      console.log(`Fetching category values for type ID: ${categoryTypeId}`);
      const response = await settingsService.getCategoryValues(categoryTypeId, {
        limit: 1000 // Request all values (up to 1000)
      });
      
      console.log('Category values response:', response);
      
      // Extract category values from the response
      const categoryValuesData = response.category_values || 
                               (response.data && response.data.category_values) || 
                               [];
      
      // Sort values alphabetically
      const sortedValues = [...categoryValuesData].sort((a, b) => 
        a.value_key.localeCompare(b.value_key)
      );
      
      setCategoryValues(sortedValues);
    } catch (err) {
      console.error('Error fetching category values:', err);
      setError(t('failed_to_load_category_values'));
    } finally {
      setLoadingValues(false);
    }
  };

  // Initial fetch of category types
  useEffect(() => {
    console.log('CategoryManagement component mounted');
    fetchCategoryTypes();
    
    // Cleanup function
    return () => {
      console.log('CategoryManagement component unmounted');
    };
  }, []);

  // Handle search term changes
  useEffect(() => {
    if (searchTerm) {
      // If searching, fetch from API
      fetchCategoryTypes(true);
    } else {
      // If not searching, filter locally
      filterCategoryTypes('');
    }
  }, [searchTerm]);

  // Fetch category values when a category type is selected
  useEffect(() => {
    if (selectedCategoryType) {
      fetchCategoryValues(selectedCategoryType.category_type_id);
    } else {
      setCategoryValues([]);
    }
  }, [selectedCategoryType]);

  const formatDate = settingsService.formatDate;

  const handleSearch = (term) => {
    setSearchTerm(term);
  };

  const handleRefresh = () => {
    // Clear cache and force refresh
    storageUtils.clearCachedCategoryTypes();
    setSearchTerm('');
    fetchCategoryTypes(true);
  };

  const handleSelectCategoryType = (catType) => {
    setSelectedCategoryType(catType);
    setEditingCategoryType(false);
    setEditingCategoryValue(false);
  };

  const handleEditCategoryType = () => {
    if (selectedCategoryType) {
      setNewCategoryType({
        category_name: selectedCategoryType.category_name,
        description: selectedCategoryType.description || ''
      });
      setEditingCategoryType(true);
    }
  };

  const handleNewCategoryType = () => {
    setSelectedCategoryType(null);
    setNewCategoryType({
      category_name: '',
      description: ''
    });
    setEditingCategoryType(true);
  };

  const handleCategoryTypeChange = (e) => {
    const { name, value } = e.target;
    setNewCategoryType(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSaveCategoryType = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      // Validate required fields
      if (!newCategoryType.category_name) {
        throw new Error(t('category_name_required'));
      }
      
      let response;
      
      if (selectedCategoryType) {
        // Update existing category type
        console.log('Updating category type:', selectedCategoryType.category_type_id, newCategoryType);
        response = await settingsService.updateCategoryType(
          selectedCategoryType.category_type_id, 
          newCategoryType
        );
        setSuccess(t('category_type_updated_successfully'));
      } else {
        // Create new category type
        console.log('Creating new category type:', newCategoryType);
        response = await settingsService.createCategoryType(newCategoryType);
        setSuccess(t('category_type_created_successfully'));
      }
      
      console.log('Save category type response:', response);
      
      // Clear cache to ensure fresh data
      storageUtils.clearCachedCategoryTypes();
      
      // Refresh category types to get the updated list
      await fetchCategoryTypes(true);
      
      // If we updated an existing type, select it again
      if (selectedCategoryType && response.data) {
        const updatedType = response.data;
        setSelectedCategoryType(updatedType);
      } else if (response.data) {
        // If we created a new type, select it
        setSelectedCategoryType(response.data);
      }
      
      setEditingCategoryType(false);
    } catch (err) {
      console.error('Error saving category type:', err);
      setError(err.message || t('failed_to_save_category_type'));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCategoryType = async () => {
    if (!selectedCategoryType) return;
    
    if (!window.confirm(t('confirm_delete_category_type'))) return;
    
    setLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      console.log('Deleting category type:', selectedCategoryType.category_type_id);
      await settingsService.deleteCategoryType(selectedCategoryType.category_type_id);
      
      // Clear cache to ensure fresh data
      storageUtils.clearCachedCategoryTypes();
      
      // Refresh category types
      await fetchCategoryTypes(true);
      
      setSelectedCategoryType(null);
      setCategoryValues([]);
      setSuccess(t('category_type_deleted_successfully'));
    } catch (err) {
      console.error('Error deleting category type:', err);
      setError(t('failed_to_delete_category_type'));
    } finally {
      setLoading(false);
    }
  };

  const handleNewCategoryValue = () => {
    setNewCategoryValue({
      value_key: '',
      is_active: 'true',
      description: ''
    });
    setEditingCategoryValue(true);
  };

  const handleEditCategoryValue = (catValue) => {
    setNewCategoryValue({
      category_value_id: catValue.category_value_id,
      value_key: catValue.value_key,
      is_active: catValue.is_active,
      description: catValue.description || ''
    });
    setEditingCategoryValue(true);
  };

  const handleCategoryValueChange = (e) => {
    const { name, value } = e.target;
    setNewCategoryValue(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSaveCategoryValue = async () => {
    if (!selectedCategoryType) return;
    
    setLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      // Validate required fields
      if (!newCategoryValue.value_key) {
        throw new Error(t('value_key_required'));
      }
      
      if (newCategoryValue.category_value_id) {
        // Update existing category value
        console.log('Updating category value:', newCategoryValue.category_value_id, newCategoryValue);
        await settingsService.updateCategoryValue(
          newCategoryValue.category_value_id, 
          newCategoryValue
        );
        setSuccess(t('category_value_updated_successfully'));
      } else {
        // Create new category value
        console.log('Creating new category value for type:', selectedCategoryType.category_type_id, newCategoryValue);
        await settingsService.createCategoryValue(
          selectedCategoryType.category_type_id, 
          newCategoryValue
        );
        setSuccess(t('category_value_created_successfully'));
      }
      
      // Refresh category values for the selected type
      await fetchCategoryValues(selectedCategoryType.category_type_id);
      
      setEditingCategoryValue(false);
    } catch (err) {
      console.error('Error saving category value:', err);
      setError(err.message || t('failed_to_save_category_value'));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCategoryValue = async (catValue) => {
    if (!window.confirm(t('confirm_delete_category_value'))) return;
    
    setLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      console.log('Deleting category value:', catValue.category_value_id);
      await settingsService.deleteCategoryValue(catValue.category_value_id);
      
      // Refresh category values for the selected type
      await fetchCategoryValues(selectedCategoryType.category_type_id);
      
      setSuccess(t('category_value_deleted_successfully'));
    } catch (err) {
      console.error('Error deleting category value:', err);
      setError(t('failed_to_delete_category_value'));
    } finally {
      setLoading(false);
    }
  };

  const handleClearSearch = () => {
    setSearchTerm('');
  };

  return (
    <div className={styles.categoriesTab}>
      <div className={styles.categoriesHeader}>
        <div className={styles.searchContainer}>
          <div className={styles.searchInputWrapper}>
            <FontAwesomeIcon icon={faSearch} className={styles.searchIcon} />
            <input
              type="text"
              className={styles.searchInput}
              placeholder={t('search_category_types')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button 
                className={styles.clearSearchButton}
                onClick={handleClearSearch}
                aria-label="Clear search"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            )}
          </div>
        </div>
        <button 
          className={styles.refreshButton}
          onClick={handleRefresh}
          disabled={isRefreshing}
        >
          <FontAwesomeIcon icon={faSync} spin={isRefreshing} />
          <span>{isRefreshing ? t('refreshing') : t('refresh')}</span>
        </button>
      </div>
      
      <div className={styles.categoriesContent}>
        <div className={styles.categoriesSidebar}>
          <div className={styles.sidebarHeader}>
            <h3 className={styles.sidebarTitle}>{t('category_types')}</h3>
            <button
              className={styles.addCategoryButton}
              onClick={handleNewCategoryType}
              disabled={loading}
            >
              <FontAwesomeIcon icon={faPlus} />
            </button>
          </div>
          
          {loading && !editingCategoryType && !editingCategoryValue && !isRefreshing ? (
            <div className={styles.loading}>
              <div className={styles.spinner}></div>
              <p>{t('loading_categories')}</p>
            </div>
          ) : filteredCategoryTypes.length > 0 ? (
            <div className={styles.categoryTypesList}>
              <div className={styles.categoryTypesListHeader}>
                <div className={styles.categoryTypeItemRow}>
                  <span className={styles.categoryTypeItemNumber}>#</span>
                  <span className={styles.categoryTypeItemId}>ID</span>
                  <span className={styles.categoryTypeItemName}>{t('name')}</span>
                </div>
              </div>
              {filteredCategoryTypes.map((catType, index) => (
                <div
                  key={catType.category_type_id}
                  className={`${styles.categoryTypeItem} ${selectedCategoryType?.category_type_id === catType.category_type_id ? styles.selectedCategoryType : ''}`}
                  onClick={() => handleSelectCategoryType(catType)}
                >
                  <div className={styles.categoryTypeItemRow}>
                    <span className={styles.categoryTypeItemNumber}>{index + 1}</span>
                    <span className={styles.categoryTypeItemId}>{catType.category_type_id}</span>
                    <span className={styles.categoryTypeItemName}>{catType.category_name}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className={styles.emptyState}>
              <p>{searchTerm ? t('no_matching_category_types') : t('no_category_types_found')}</p>
            </div>
          )}
        </div>
        
        <div className={styles.categoriesMain}>
          {editingCategoryType ? (
            <div className={styles.categoryTypeForm}>
              <h3 className={styles.formTitle}>
                {selectedCategoryType ? t('edit_category_type') : t('add_category_type')}
              </h3>
              
              <div className={styles.formGroup}>
                <label htmlFor="category_name" className={styles.formLabel}>
                  {t('category_name')} <span className={styles.required}>*</span>
                </label>
                <input
                  type="text"
                  id="category_name"
                  name="category_name"
                  className={styles.formInput}
                  value={newCategoryType.category_name}
                  onChange={handleCategoryTypeChange}
                  disabled={loading}
                  required
                />
                <div className={styles.formHelp}>{t('category_name_help')}</div>
              </div>
              
              <div className={styles.formGroup}>
                <label htmlFor="description" className={styles.formLabel}>
                  {t('description')}
                </label>
                <textarea
                  id="description"
                  name="description"
                  className={styles.formTextarea}
                  value={newCategoryType.description}
                  onChange={handleCategoryTypeChange}
                  disabled={loading}
                  rows={4}
                ></textarea>
              </div>
              
              <div className={styles.formActions}>
                <button
                  type="button"
                  className={styles.cancelButton}
                  onClick={() => setEditingCategoryType(false)}
                  disabled={loading}
                >
                  {t('cancel')}
                </button>
                <button
                  type="button"
                  className={styles.saveButton}
                  onClick={handleSaveCategoryType}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <div className={styles.spinner}></div>
                      <span>{t('saving')}</span>
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faEdit} />
                      <span>{t('save')}</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          ) : editingCategoryValue ? (
            <div className={styles.categoryValueForm}>
              <h3 className={styles.formTitle}>
                {newCategoryValue.category_value_id ? t('edit_category_value') : t('add_category_value')}
              </h3>
              
              <div className={styles.formGroup}>
                <label htmlFor="value_key" className={styles.formLabel}>
                  {t('value_key')} <span className={styles.required}>*</span>
                </label>
                <input
                  type="text"
                  id="value_key"
                  name="value_key"
                  className={styles.formInput}
                  value={newCategoryValue.value_key}
                  onChange={handleCategoryValueChange}
                  disabled={loading}
                  required
                />
                <div className={styles.formHelp}>{t('value_key_help')}</div>
              </div>
              
              <div className={styles.formGroup}>
                <label htmlFor="is_active" className={styles.formLabel}>
                  {t('status')}
                </label>
                <select
                  id="is_active"
                  name="is_active"
                  className={styles.formSelect}
                  value={newCategoryValue.is_active}
                  onChange={handleCategoryValueChange}
                  disabled={loading}
                >
                  <option value="true">{t('active')}</option>
                  <option value="false">{t('inactive')}</option>
                </select>
              </div>
              
              <div className={styles.formGroup}>
                <label htmlFor="value_description" className={styles.formLabel}>
                  {t('description')}
                </label>
                <textarea
                  id="value_description"
                  name="description"
                  className={styles.formTextarea}
                  value={newCategoryValue.description}
                  onChange={handleCategoryValueChange}
                  disabled={loading}
                  rows={4}
                ></textarea>
              </div>
              
              <div className={styles.formActions}>
                <button
                  type="button"
                  className={styles.cancelButton}
                  onClick={() => setEditingCategoryValue(false)}
                  disabled={loading}
                >
                  {t('cancel')}
                </button>
                <button
                  type="button"
                  className={styles.saveButton}
                  onClick={handleSaveCategoryValue}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <div className={styles.spinner}></div>
                      <span>{t('saving')}</span>
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faEdit} />
                      <span>{t('save')}</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          ) : selectedCategoryType ? (
            <div className={styles.categoryTypeDetails}>
              <div className={styles.categoryTypeHeader}>
                <div className={styles.categoryTypeInfo}>
                  <div className={styles.categoryTypeIdBadge}>
                    ID: {selectedCategoryType.category_type_id}
                  </div>
                  <h3 className={styles.categoryTypeName}>{selectedCategoryType.category_name}</h3>
                  <p className={styles.categoryTypeDescription}>{selectedCategoryType.description}</p>
                </div>
                <div className={styles.categoryTypeActions}>
                  <button
                    className={styles.editButton}
                    onClick={handleEditCategoryType}
                    disabled={loading}
                  >
                    <FontAwesomeIcon icon={faEdit} />
                    <span>{t('edit')}</span>
                  </button>
                  <button
                    className={styles.deleteButton}
                    onClick={handleDeleteCategoryType}
                    disabled={loading}
                  >
                    <FontAwesomeIcon icon={faTrashAlt} />
                    <span>{t('delete')}</span>
                  </button>
                </div>
              </div>
              
              <div className={styles.categoryValuesSection}>
                <div className={styles.categoryValuesHeader}>
                  <div className={styles.categoryValuesSectionInfo}>
                    <h4 className={styles.categoryValuesSectionTitle}>{t('category_values')}</h4>
                    <span className={styles.categoryValuesCount}>
                      {categoryValues.length} {categoryValues.length === 1 ? t('value') : t('values')}
                    </span>
                  </div>
                  <button
                    className={styles.addValueButton}
                    onClick={handleNewCategoryValue}
                    disabled={loading}
                  >
                    <FontAwesomeIcon icon={faPlus} />
                    <span>{t('add_value')}</span>
                  </button>
                </div>
                
                {loadingValues ? (
                  <div className={styles.loading}>
                    <div className={styles.spinner}></div>
                    <p>{t('loading_category_values')}</p>
                  </div>
                ) : categoryValues.length > 0 ? (
                  <div className={styles.categoryValuesTable}>
                    <div className={styles.categoryValuesTableHeader}>
                      <div className={styles.categoryValuesTableCell}>#</div>
                      <div className={styles.categoryValuesTableCell}>ID</div>
                      <div className={styles.categoryValuesTableCell}>{t('value_key')}</div>
                      <div className={styles.categoryValuesTableCell}>{t('status')}</div>
                      <div className={styles.categoryValuesTableCell}>{t('description')}</div>
                      <div className={styles.categoryValuesTableCell}>{t('actions')}</div>
                    </div>
                    <div className={styles.categoryValuesTableBody}>
                      {categoryValues.map((catValue, index) => (
                        <div key={catValue.category_value_id} className={styles.categoryValuesTableRow}>
                          <div className={styles.categoryValuesTableCell}>{index + 1}</div>
                          <div className={styles.categoryValuesTableCell}>{catValue.category_value_id}</div>
                          <div className={styles.categoryValuesTableCell}>{catValue.value_key}</div>
                          <div className={styles.categoryValuesTableCell}>
                            <span className={`${styles.statusBadge} ${catValue.is_active === 'true' ? styles.statusActive : styles.statusInactive}`}>
                              {catValue.is_active === 'true' ? t('active') : t('inactive')}
                            </span>
                          </div>
                          <div className={styles.categoryValuesTableCell}>{catValue.description}</div>
                          <div className={styles.categoryValuesTableCell}>
                            <div className={styles.categoryValueActions}>
                              <button
                                className={styles.editValueButton}
                                onClick={() => handleEditCategoryValue(catValue)}
                                disabled={loading}
                              >
                                <FontAwesomeIcon icon={faEdit} />
                              </button>
                              <button
                                className={styles.deleteValueButton}
                                onClick={() => handleDeleteCategoryValue(catValue)}
                                disabled={loading}
                              >
                                <FontAwesomeIcon icon={faTrashAlt} />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className={styles.emptyState}>
                    <p>{t('no_category_values_found')}</p>
                  </div>
                )}
              </div>
              
              <div className={styles.metadataSection}>
                <h4 className={styles.metadataSectionTitle}>{t('metadata')}</h4>
                <div className={styles.metadataGrid}>
                  <div className={styles.metadataItem}>
                    <div className={styles.metadataLabel}>{t('created_at')}</div>
                    <div className={styles.metadataValue}>{formatDate(selectedCategoryType.created_at)}</div>
                  </div>
                  <div className={styles.metadataItem}>
                    <div className={styles.metadataLabel}>{t('updated_at')}</div>
                    <div className={styles.metadataValue}>{formatDate(selectedCategoryType.updated_at)}</div>
                  </div>
                  {selectedCategoryType.created_by && (
                    <div className={styles.metadataItem}>
                      <div className={styles.metadataLabel}>{t('created_by')}</div>
                      <div className={styles.metadataValue}>{selectedCategoryType.created_by}</div>
                    </div>
                  )}
                  {selectedCategoryType.updated_by && (
                    <div className={styles.metadataItem}>
                      <div className={styles.metadataLabel}>{t('updated_by')}</div>
                      <div className={styles.metadataValue}>{selectedCategoryType.updated_by}</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className={styles.noSelectionState}>
              <FontAwesomeIcon icon={faEdit} size="2x" />
              <p>{t('select_category_type_or_create_new')}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CategoryManagement;